import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'

import { IncotermCode } from '@freighthub/freighthub-corelib'
import { Sheet } from '@freighthub/freighthub-quotation-sdk'

import Client from '@/api/client'
import { getConfig } from '@/app/config'
import { TagDTO, WaypointBaseDTOTransportMode } from '@/clients/booking'
import { CreateEntityDocumentProps } from '@/features/documents'

import { getLegalEntityDetails } from './masterData'
import { MQDocument } from '../features/wizard/types/BookingWizardFormState'

const basePath = '/api/shipping'
const documentsApiUrl = `/api/documents`
const client = new Client(getConfig().API_ENDPOINT ?? '')

export type TransportToPortMode = 'truck' | 'intermodal' | 'barge'

export type MQAdditionalRequirements = {
  isHeavyTestedContainer: boolean
  letterOfCreditRequired: boolean
  maxPayloadContainer: string
  maxDemurrageTime: string
  EXWDate: string
  transportToPortMode: TransportToPortMode
  transportCompanyPreference: string
}

export type BuyersConsolidationQuoteServices = {
  enabled: boolean
  numberOfShipments?: number
  incoterms: IncotermCode[]
}

export type RequestManualQuoteDTO = {
  quoteSheetId: string
  customerNotes: string
  modes?: WaypointBaseDTOTransportMode[]
  pickupAddress?: string
  deliveryAddress?: string
  cargoReadyAt?: Date
  additionalRequirements?: MQAdditionalRequirements
  buyersConsolidation?: BuyersConsolidationQuoteServices
  incoterm?: string
  bookingParty?: string
}

const requestManualQuote = async (dto: RequestManualQuoteDTO) => {
  return await client.post<{ manualQuoteID: string }>(
    `${basePath}/api/v1/quoteSheets/${dto.quoteSheetId}/requestManualQuote/`,
    dto
  )
}

const uploadMQDocument = async (props: CreateEntityDocumentProps) => {
  const { file, ...rest } = props.data
  return await client.postFile<Document>(`${documentsApiUrl}/documents/manual-quote/${props.entityId}`, file, {
    json: JSON.stringify(rest),
  })
}

const getQuoteSheet = async (quoteSheetId: string) => {
  return await client.get<Sheet>(`${basePath}/api/v1/quoteSheets/${quoteSheetId}`)
}

type BookQuoteSheetContact = {
  contactID?: string
  legalEntityID?: string
  companyName?: string
  address?: string
  contactPerson?: string
  phone?: string
  email?: string
}

export type BookQuoteSheetParams = {
  quoteOptionId: string
  quoteSheetId: string
  bookedByFreightHub: boolean
  shipperContact: BookQuoteSheetContact
  consigneeContact: BookQuoteSheetContact
  legalEntityId?: string | null
  tags?: TagDTO[]
  hsCodesCount?: number
  quoteSheet?: Sheet
}

const bookQuoteSheet = async ({ quoteSheetId, quoteOptionId, quoteSheet, ...params }: BookQuoteSheetParams) => {
  const legalEntity = await getLegalEntityDetails(params.legalEntityId ?? '')

  const optionsIdx = quoteSheet?.response.options.findIndex((option) => option._id?.toString() === quoteOptionId)

  const { shipmentId } = await client.post<{ shipmentId: string }>(
    `${basePath}/api/v1/quoteSheets/${quoteSheetId}/book`,
    {
      ...params,
      option: optionsIdx,
      billingAddress: legalEntity?.billing?.address,
      eoriNumber: legalEntity?.billing?.details?.eoriNumber,
    }
  )

  return { shipmentId }
}

const uploadFiles = async (files: MQDocument[], manualQuoteID: string, customerID: string) => {
  // We map over the files and upload them one by one because the API does not support multiple file uploads
  const promiseCollection = files.map((file) => {
    return uploadMQDocument({
      entityId: manualQuoteID,
      data: {
        file: file.file,
        state: 'published',
        mimeType: file.file.type,
        customerID,
        source: 'customer',
        documentTypeCode: file.role,
      },
    })
  })
  await Promise.all(promiseCollection)
}

type UploadManualQuoteDocumentVariables = { manualQuoteID: string; manualQuoteDocuments: MQDocument[] }

export const useUploadMQDocumentMutation = (customerID: string) => {
  return useMutation({
    mutationFn: async ({ manualQuoteDocuments, manualQuoteID }: UploadManualQuoteDocumentVariables) =>
      uploadFiles(manualQuoteDocuments, manualQuoteID, customerID),
  })
}

export const useCreateMQMutation = () => {
  return useMutation({ mutationFn: (dto: RequestManualQuoteDTO) => requestManualQuote(dto) })
}

export const useCreateAndWaitShipmentMutation = () => {
  return useMutation({
    mutationFn: (params: BookQuoteSheetParams) => bookQuoteSheet(params),
  })
}

export const useGetQuoteSheetQuery = (quoteSheetId: string) => {
  return useSuspenseQuery({
    queryFn: () => getQuoteSheet(quoteSheetId),
    queryKey: ['quoteSheet', quoteSheetId],
    refetchOnWindowFocus: false,
  })
}

/**
 * Resolves a quote sheet ID to its connected manual-quote ID
 * (`request.manualQuoteRequest.id`), which is the identifier the documents API
 * expects as `manualQuoteID`. Disabled (and resolves to `undefined`) when no
 * quote sheet ID is provided.
 */
export const useConnectedQuoteID = (quoteSheetID?: string): { connectedQuoteID?: string; isResolving: boolean } => {
  const quoteSheetQuery = useQuery({
    queryKey: ['quoteSheet', quoteSheetID],
    queryFn: () => getQuoteSheet(quoteSheetID as string),
    enabled: !!quoteSheetID,
  })

  return {
    connectedQuoteID: quoteSheetQuery.data?.request.manualQuoteRequest?.id,
    isResolving: quoteSheetQuery.isLoading,
  }
}
